/**
 * The checks a recording must pass before any upload network call.
 *
 * Shared by the signed-in recorder and the anonymous funnel. The server stays
 * authoritative on the size cap; this check only spares the user an upload the
 * server is certain to refuse.
 */
import {
  normalizeUploadContentType,
  UPLOAD_MIN_BLOB_BYTES,
  type UploadContentType,
} from "@reflect/domain";
import { ERROR_MESSAGE_KEYS, type ErrorMessageKey, type RecordMessageKey } from "@reflect/i18n";

/** Why pre-flight refused a recording. */
export type PreflightRefusal = "too-short" | "unsupported-format" | "too-large";

/** The catalog key each {@link PreflightRefusal} renders as. */
export const PREFLIGHT_MESSAGE_KEYS: Readonly<
  Record<PreflightRefusal, ErrorMessageKey | RecordMessageKey>
> = Object.freeze({
  "too-short": "web.record.error.tooShort",
  "unsupported-format": "web.record.error.unsupportedFormat",
  "too-large": ERROR_MESSAGE_KEYS.upload_too_large,
});

/** The outcome of {@link preflightRecording}. */
export type PreflightResult =
  { ok: true; contentType: UploadContentType } | { ok: false; refusal: PreflightRefusal };

/**
 * Check a recording in the order the refusals are reported: too short to be a
 * take, a container the upload allowlist does not accept, then over the cap.
 *
 * @param recording - the assembled take.
 * @param maxUploadBytes - the byte cap the server will enforce for this caller.
 */
export function preflightRecording(recording: Blob, maxUploadBytes: number): PreflightResult {
  if (recording.size < UPLOAD_MIN_BLOB_BYTES) return { ok: false, refusal: "too-short" };
  const contentType = normalizeUploadContentType(recording.type);
  if (contentType === null) return { ok: false, refusal: "unsupported-format" };
  if (recording.size > maxUploadBytes) return { ok: false, refusal: "too-large" };
  return { ok: true, contentType };
}
